import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import MultiTable from "../../../Common/MUITable";
import { ClearedPointDetailModal } from "./VehicleClearedPointsModals";
import DelhiReportExport from "./DelhiReportExport";
import { GetVehicleHaltPointDetails } from "./ReportApi";

const NAVY = "#003366";

const formatTime = (value) => {
  if (!value) return "—";
  const timePart = String(value).includes("T")
    ? String(value).split("T")[1]
    : String(value);
  return timePart.slice(0, 5);
};

const VehicleClearedPointsInnerReport = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const parentRow = location.state?.parentRow;
  const parentRows = location.state?.parentRows || [];
  const showAllPoints = Boolean(location.state?.showAllPoints);
  const coverageParent = location.state?.coverageParent;

  const [detailPoint, setDetailPoint] = useState(null);
  const [showDetail, setShowDetail] = useState(false);
  const [haltPoints, setHaltPoints] = useState([]);
  const [loading, setLoading] = useState(false);
  const circleId =
    parentRow?.circleId ||
    parentRow?.wardId ||
    coverageParent?.circleId ||
    coverageParent?.wardId ||
    undefined;
  const vehicleId = showAllPoints ? undefined : parentRow?.vehicleId || undefined;

  useEffect(() => {
    let isMounted = true;
    const fetchHaltPoints = async () => {
      try {
        setLoading(true);
        let data = [];

        if (showAllPoints) {
          const res = await GetVehicleHaltPointDetails({ circleId });
          if (res?.data) {
            data = Array.isArray(res.data) ? res.data : res.data.data || [];
          }
        } else {
          const res = await GetVehicleHaltPointDetails({ circleId, vehicleId });
          if (res?.data) {
            data = Array.isArray(res.data) ? res.data : res.data.data || [];
          }
        }
        
        const mappedData = data.map((point) => ({
          ...point,
          timeDisplay: formatTime(point.time || point.clearedAt),
          status: point.status || "Cleared",
        }));
        
        if (isMounted) {
          setHaltPoints(mappedData);
        }
      } catch (error) {
        console.error("Error fetching cleared halt points:", error);
        if (isMounted) {
          setHaltPoints([]);
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchHaltPoints();
    return () => {
      isMounted = false;
    };
  }, []);

  const handleBack = () => {
    navigate("/vehicle-coverage-report", {
      state: coverageParent ? { parentRow: coverageParent } : undefined,
    });
  };

  const exportColumns = useMemo(() => {
    const base = [
      { label: "Halt Point", field: "haltPoint" },
      { label: "Time", field: "timeDisplay" },
      { label: "Status", field: "status" },
    ];
    if (showAllPoints) {
      return [{ label: "Vehicle Number", field: "vehicleNo" }, ...base];
    }
    return base;
  }, [showAllPoints]);

  const tableColumns = useMemo(() => {
    const cols = [];
    if (showAllPoints) {
      cols.push({
        label: "Vehicle Number",
        field: "vehicleNo",
        width: "140px",
      });
    }
    cols.push(
      {
        label: "Halt Point",
        field: "haltPoint",
        width: "160px",
      },
      {
        label: "Time",
        field: "timeDisplay",
        width: "120px",
      },
      {
        label: "Status",
        field: "status",
        width: "120px",
      },
      {
        label: "Action",
        field: "action",
        width: "100px",
        render: (row) => (
          <button
            type="button"
            className="btn btn-link p-0"
            title="View"
            onClick={() => {
              setDetailPoint(row);
              setShowDetail(true);
            }}
            style={{ color: NAVY, fontSize: "18px" }}
          >
            <i className="fa-solid fa-eye" />
          </button>
        ),
      }
    );
    return cols;
  }, [showAllPoints]);

  return (
    <div className="content text-start">
      <div className="page-content">
        <div className="d-flex flex-column flex-md-row justify-content-between align-items-start gap-3 mt-3 mb-3">
          <div className="me-md-3">
            <h5 className="mb-1 fw-semibold">
              Cleared Points
              {showAllPoints
                ? " - All"
                : parentRow?.vehicleNo
                  ? ` - ${parentRow.vehicleNo}`
                  : ""}
            </h5>
            <p className="text-muted mb-0">
              View cleared halt points
              {showAllPoints
                ? " for all vehicles."
                : " for the selected vehicle."}
            </p>
          </div>
          <div className="d-flex flex-wrap gap-2">
            <button
              type="button"
              className="button-container"
              onClick={handleBack}
            >
              Back
            </button>
            <DelhiReportExport
              reportData={haltPoints}
              reportTitle="Cleared Points"
              columns={exportColumns}
              fileName="ClearedPointsReport"
            />
          </div>
        </div>

        <div className="card shadow">
          <div className="card-body">
            <div className="fw-semibold mb-3">
              Vehicle Number :{" "}
              {showAllPoints ? "All" : parentRow?.vehicleNo || "—"}
            </div>

            <div className="table-responsive">
              <MultiTable
                needSRNumber
                columns={tableColumns}
                rows={haltPoints}
                tableHeight={420}
                rowsPerPageOptions={[10, 25, 50, 100]}
                loading={loading}
              />
            </div>
          </div>
        </div>
      </div>

      <ClearedPointDetailModal
        show={showDetail}
        point={detailPoint}
        circleId={circleId}
        vehicleId={detailPoint?.vehicleId || vehicleId}
        onClose={() => {
          setShowDetail(false);
          setDetailPoint(null);
        }}
      />
    </div>
  );
};

export default VehicleClearedPointsInnerReport;
